import { applicationType, DEFAULT_ORG } from '@src/globals';
import { logout } from '@api/logout';
import { message } from 'antd';
import { isNativeApp } from 'src/utils/utils';
import { LocalStorageKeys, LocalStorageService } from 'src/services/localStorage';
import type { OrgViewConfig } from 'src/utils/routeWarmer';
import Cookies from 'universal-cookie';
const AUTHENTICATION_ERROR="AUTHENTICATION_ERROR"

export const getAuthenticationHeaders = async () => {
  return {
    'user-id': await LocalStorageService.get(LocalStorageKeys.___USER_ID__),
    'organisation-id': await LocalStorageService.get(LocalStorageKeys.__ORGANISATION_ID__),
    'access-token': await LocalStorageService.get(LocalStorageKeys.__AUTH_TOKEN__),
    'application-type': applicationType,
  };
};

export const isAuthenticationHeadersPresent = () => {
  return (
    LocalStorageService.exists(LocalStorageKeys.___USER_ID__)
    && LocalStorageService.exists(LocalStorageKeys.__ORGANISATION_ID__)
    && LocalStorageService.exists(LocalStorageKeys.__AUTH_TOKEN__)
  );
};

export const getErrorMessage = (err) => {
  let errorMessage = '';
  if (!err.response) {
    errorMessage = 'Could not connect. Please check your internet connection';
  } else {
    const responseData = err.response.data;
    if (responseData && responseData.error) {
      errorMessage = responseData.error.message;
    } else {
      errorMessage = `Something went wrong\n${err.message}`;
    }
  }
  return errorMessage;
};

export const getErrorFailures = (err) => {
  let errorFailures = [];
  if (err.response) {
    const responseData = err.response.data;
    if (responseData && responseData.error) {
      errorFailures = Array.isArray(responseData.error.failures) &&
        responseData.error.failures.length ? responseData.error.failures : [];
    }
  }
  return errorFailures;
};

export const getErrorMessageForBlob = async (err) => {
  let errorMessage = '';
  if (!err.response) {
    errorMessage = 'Could not connect. Please check your internet connection';
  } else {
    const responseData = await new Response(err.response.data).json();
    console.log(responseData);
    if (responseData && responseData.error) {
      errorMessage = responseData.error.message;
    } else {
      errorMessage = `Something went wrong\n${err.message}`;
    }
  }
  return errorMessage;
};
export const getErrorForBlob = async (err) => {
  let error: any = {};
  if (!err.response) {
    error.message = 'Could not connect. Please check your internet connection';
  } else {
    const responseData = await new Response(err.response.data).json();
    console.log(responseData);
    if (responseData && responseData.error) {
      error = responseData.error;
    } else {
      error.message = `Something went wrong\n${err.message}`;
    }
  }
  return error;
};

export const generateQueryString = (paramsArray) => {
  const queryString = paramsArray
    .map(({ key, val, isArray }) => {
      if (isArray) {
        return val
          .map((x) => `${key}=${encodeURIComponent(x.label)}`)
          .filter((elem: any) => elem !== null)
          .join('&');
      }
      if (val || val === 0 || val === false) {
        return `${key}=${encodeURIComponent(val)}`;
      }
      return null;
    })
    .filter((elem: any) => elem !== null)
    .join('&');
  return queryString;
};
export const generateQueryStringFromObject = (obj) => {
  if (!obj) {
    return '';
  }
  const keys = Object.keys(obj);
  const x = keys.map((key) => {
    return { key, val: obj[key], isArray: Array.isArray(obj[key]) };
  });
  return generateQueryString(x);
};
export const generateObjectFromList = (key, list) => {
  const generatedList: any = [];
  if (list) {
    list.forEach((element) => {
      generatedList.push({ key, val: element });
    });
  }
  return generatedList;
};

export const getFileExtension = (file) => {
  return file.name.split('.').pop();
};

export const dataHeaders = {
  JPG: 'image/*',
  JPEG: 'image/*',
  PNG: 'image/*',
  WEBP: 'image/*',
  PDF: 'application/*',
  DOCX: 'application/*',
  XLSX: 'application/*',
  CSV: 'text/csv',
  XLS: 'application/*',
  DOC: 'application/*',
};

export const clearUserCredentialFromCache= () => {
  const cookies = new Cookies();
  cookies.remove('is_authenticated', { path: '/' });
  LocalStorageService.remove(LocalStorageKeys.___USER_ID__);
  LocalStorageService.remove(LocalStorageKeys.__AUTH_TOKEN__);
  LocalStorageService.remove(LocalStorageKeys.__ORGANISATION_ID__);
  LocalStorageService.remove(LocalStorageKeys.SHIPSY_TIMEZONE);
};

const handlePasswordChangeLogout = () => {
  clearUserCredentialFromCache();
  window.location.href = '/login';
};

export const logoutUserAndClearCache = async (isFromChangePassword = false) => {
  if (isFromChangePassword) {
    handlePasswordChangeLogout();
    return;
  }
  const response = await logout();
    if (response.isSuccess) {
      clearUserCredentialFromCache();
      message.success('User Logged out succesfully');
      window.location.href = '/login';
    } else if(response.errCode==AUTHENTICATION_ERROR) {
      clearUserCredentialFromCache();
      window.location.href = '/login';
    } else{
      message.error('Logout failed. Please try again');
    } 
};

export const isAuthenticationFlowPage = () => {
  return (
    [
      '/login',
      '/forgot-password',
      '/login/',
      '/forgot-password/',
      '/ssoRedirect',
      '/ssoRedirect/',
      '/signInFromInternal/',
      '/signInFromInternal',
      '/login/cli-consent',
      '/login/cli-consent/',
      '/p/track',
      '/p/track/',
      '/p/ip-restricted',
      '/p/ip-restricted/',
    ].includes(window.location.pathname) ||
    window.location.pathname?.includes('/oauth/oidc')
  );
};

export const redirectToLoginPage = () => {
  if (!isAuthenticationFlowPage()) {
    window.location.href = '/login';
  }
};

export const getURLConfigs = () => {
  const origin = window.location.hostname;
  let organisationPrettyName = '';

  if (origin.indexOf('.') > 0) {
    organisationPrettyName = origin.substring(0, origin.indexOf('.'));
  } else {
    organisationPrettyName = DEFAULT_ORG;
  }

  return {
    origin,
    organisationPrettyName,
  };
};

export const redirectToUrl = (organisationDetails) => {
  const redirectToFromOrgDetails = organisationDetails.ops_login_redirect_to;
  const redirectToFromLogin = LocalStorageService.getRaw(LocalStorageKeys.__REDIRECT_TO_VIEW_TYPE__);
  const redirectTo = redirectToFromLogin && redirectToFromLogin !== 'undefined' ? redirectToFromLogin : redirectToFromOrgDetails;

  // An explicit path target (e.g. a deep-link from the native app's side menu, passed via
  // ops_login_redirect_to) wins over both the native-app Co-Pilot default and the view-type switch
  // below. The switch only understands view-type keywords ('landingPageNew', etc.), so a raw path
  // would otherwise fall through to the default; and the native app would otherwise be forced to
  // Co-Pilot. A leading '/' distinguishes a real path from a keyword; reject a leading '//' so a
  // protocol-relative value (e.g. '//evil.com') can't turn this into an open redirect (mirrors the
  // same-origin guard in SSORedirect.tsx).
  if (
    typeof redirectTo === 'string' &&
    redirectTo.startsWith('/') &&
    !redirectTo.startsWith('//')
  ) {
    return redirectTo;
  }

  if(isNativeApp()){
    return '/ops/co-pilot';
  }

  switch (redirectTo) {
      case 'landingPageNew':
        return '/ops/home';
      case 'manage':
          return '/dashboard/#!/manage';
      case 'setup':
          return '/dashboard/#!/setup';
      case 'ltl':
          return '/ops/domestic/track';
      case 'retail':
        return '/ops/consignments';
      case 'opsv2':
          return '/ops/consignments';
      case 'ondemand':
          return '/ops/od/reporting';
      case 'thirdParty':
          return '/ops/third-party';
      case 'hybridView':
          return '/ops/v1/consignments-list';
      case 'carrierInvoices':
          return '/ops/carrier-invoices/payables';
      case 'analytics':
          return '/ops/analytics';
      default:
          return '/';
  }
};

export const getDestinationUrl = () => {
  let destinationUrl = '';

  // decode location query string
  const decodeUrlQueryString = decodeURIComponent(window.location.search.substring(1));

  // list of query params
  const queryParamList = decodeUrlQueryString.split('&');

  // value of destination url (query field name is 'destUrl')
  queryParamList.forEach(queryParam => {
      const field = queryParam.substring(0, queryParam.indexOf('='));
      if (field === 'destUrl') {
          destinationUrl = queryParam.substring(queryParam.indexOf('=') + 1);
      }
  });

  return destinationUrl;
};

// Cache the org-level config used to warm the right route chunk in parallel with masterdata.
export const writeViewConfigCache = (config: any) => {
  const cache: OrgViewConfig = {
    ops_login_redirect_to: config?.ops_login_redirect_to,
    show_retail_options: config?.show_retail_options,
    show_logistics_express_options: config?.show_logistics_express_options,
    show_ltl_options: config?.show_ltl_options,
    show_ondemand_options: config?.show_ondemand_options,
    show_crm: config?.parts_to_show?.show_crm ?? config?.ops_dashboard_config?.parts_to_show?.show_crm,
  };
  return LocalStorageService.set(LocalStorageKeys.__ORG_VIEW_CONFIG__, JSON.stringify(cache));
};

export const setLocalStorageData = async (organisationDetails, loginDetails, stayLoggedIn = false) => {
  const cookies = new Cookies();
  const validation = loginDetails.parts_to_show || { setup:true, manage:true, analyse:true };
  const logoURL = loginDetails.logo_url || '';
  const bookingVisibleString = (loginDetails.parts_to_show && loginDetails.parts_to_show.show_agent_booking) ? 'true' : 'false';

  const itemsToRemoveFromLocalStorage = [
    LocalStorageKeys.__NAME__,
    LocalStorageKeys.___USER_ID__,
    LocalStorageKeys.__AUTH_TOKEN__,
    LocalStorageKeys.__ORGANISATION_ID__,
    LocalStorageKeys.__LOGO_URL__,
    LocalStorageKeys.__BOOKING_VISIBLE__,
    LocalStorageKeys.__PERMISSION__,
    LocalStorageKeys.__REDIRECT_TO_VIEW_TYPE__,
    LocalStorageKeys.__ORG_VIEW_CONFIG__,
    LocalStorageKeys.__REDIRECT_TO_HUB__,
    LocalStorageKeys.__CURRENT_HUB_ID__,
    LocalStorageKeys.__CURRENT_HUB_CODE__,
    LocalStorageKeys.__CURRENT_HUB_NAME__,
    LocalStorageKeys.__STATE__,
    LocalStorageKeys.__CURRENT_HUB__,
    LocalStorageKeys.__EMPLOYEE_CODE__,
  ];
  itemsToRemoveFromLocalStorage.forEach(elem => LocalStorageService.remove(elem));

  const itemsToSet = {
    [LocalStorageKeys.__PERMISSION__]: JSON.stringify(validation),
    [LocalStorageKeys.__NAME__]: loginDetails.employee.full_name,
    [LocalStorageKeys.__LOGO_URL__]: logoURL,
    [LocalStorageKeys.__BOOKING_VISIBLE__]: bookingVisibleString,
    [LocalStorageKeys.__EMPLOYEE_PHONE__]: loginDetails.employee.phone,
    [LocalStorageKeys.__REDIRECT_TO_VIEW_TYPE__]: loginDetails.ops_login_redirect_to,
    [LocalStorageKeys.__REDIRECT_TO_HUB__]: JSON.stringify(loginDetails.ops_login_hub),
    [LocalStorageKeys.___USER_ID__]: loginDetails.employee.id,
    [LocalStorageKeys.__AUTH_TOKEN__]: loginDetails.access_token.id,
    [LocalStorageKeys.__ORGANISATION_ID__]: loginDetails.organisation_id,
    [LocalStorageKeys.__EMPLOYEE_CODE__]: loginDetails.employee.employee_code,
  };

  if (loginDetails.favicon_url) {
    itemsToSet[LocalStorageKeys.__FAVICON_URL__] = loginDetails.favicon_url;
  }

  for (const key in itemsToSet) {
    await LocalStorageService.set(key, itemsToSet[key]);
  }
  await writeViewConfigCache(loginDetails);

  cookies.set('is_authenticated', 'true', {
    path:'/',
    expires: new Date((new Date().getTime()) + (1000 * 60 * 60 * 24 * 365)),
    secure: true,
    sameSite: 'lax',
  });

  window.location.href = redirectToUrl(organisationDetails);
};

export const getOidcCallbackQueryParams = (params) => {
  const { code, error } = params;

  if (code) {
    return `code=${code}`;
  }

  return `error=${error}`;
};

export const mergeMultiSheetData = (routesData, routeListData) => {
    const routeListMap = {};
    
    routeListData.forEach(item => {
        const routeCode = item.route_code;
        if (!routeListMap[routeCode]) {
            routeListMap[routeCode] = [];
        }
        routeListMap[routeCode].push({
            code: item.code,
            position: parseInt(item.position, 10)
        });
    });
    
    Object.keys(routeListMap).forEach(routeCode => {
        routeListMap[routeCode].sort((a, b) => a.position - b.position);
    });
    
    return routesData.map(route => ({
        ...route,
        route_list: routeListMap[route.code] || []
    }));
};

// Helper function to convert camelCase to snake_case
export const camelToSnakeCase = (str: string): string => {
  return str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
};

// Helper function to transform object keys from camelCase to snake_case
export const transformKeysToSnakeCase = (obj: any): any => {
  if (Array.isArray(obj)) {
    return obj.map(item => transformKeysToSnakeCase(item));
  } else if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj).reduce((acc, key) => {
      const snakeKey = camelToSnakeCase(key);
      acc[snakeKey] = transformKeysToSnakeCase(obj[key]);
      return acc;
    }, {} as any);
  }
  return obj;
};
